import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../../../components/Navbar';
import PageWithAsyncData from '../../../components/PageWithAsyncData';
import {
  FullScreenLayout,
  TopBar,
  PageTitle,
  TableWrapper,
  EmptyStateText,
} from '../../../components/Admin/AdminLayout';
import { ROUTES } from '../../../routing';
import { useCmsGetAssessmentTests } from '../../../hooks/use-api/admin';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../../../components/Table/CompactTable';
import type { GetCmsAssessmentTestsResponse } from '../../../api/admin';
import {
  FiltersHorizontalWrapper,
  TextFilterPill,
} from '../../../components/elements/Filter';
import day from 'dayjs';
import CMSPageWrapper, {
  SmallButton,
} from '../../../components/Admin/CMSPageWrapper';
import { CMS_SIDEBAR_PAGES } from '../../../components/Admin/CMSSideBar';

const SecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.textRegular};
`;

const columnHelper =
  createColumnHelper<GetCmsAssessmentTestsResponse[number]>();

export default function AssessmentTestsList() {
  const { data: tests, isLoading, error, refetch } = useCmsGetAssessmentTests();
  const history = useHistory();

  const [nameFilter, setNameFilter] = React.useState('');

  const filteredTests = React.useMemo(() => {
    let filtered = tests ?? [];
    const trimmed = nameFilter.trim().toLowerCase();
    if (trimmed) {
      filtered = filtered.filter((t) => t.name.toLowerCase().includes(trimmed));
    }
    return filtered;
  }, [tests, nameFilter]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: ({ getValue }) => (
          <span style={{ fontWeight: 500 }}>{getValue()}</span>
        ),
      }),
      columnHelper.accessor('slug', {
        header: 'Slug',
        cell: ({ getValue }) => <SecondaryText>{getValue()}</SecondaryText>,
      }),
      columnHelper.accessor((row) => row.steps?.length ?? 0, {
        id: 'steps',
        header: 'Steps',
        cell: ({ row }) => (
          <SecondaryText>{row.original.steps?.length ?? 0}</SecondaryText>
        ),
      }),
      columnHelper.accessor((row) => new Date(row.updatedAt).getTime(), {
        id: 'updatedAt',
        header: 'Last Updated',
        cell: ({ row }) => (
          <SecondaryText>
            {day(row.original.updatedAt).format('DD/MM/YYYY HH:mm')}
          </SecondaryText>
        ),
      }),
    ],
    []
  );

  return (
    <PageWithAsyncData
      isLoading={isLoading}
      error={error}
      retry={refetch}
      noScroll
    >
      <Navbar
        breadCrumbs={[
          { label: 'Admin Dashboard', url: ROUTES.ADMIN.HOME.getPath() },
          { label: 'Assessment Tests', url: null },
        ]}
      />
      <CMSPageWrapper
        currentPage={CMS_SIDEBAR_PAGES.ASSESSMENT_TESTS}
        shouldAddEmptyDiv={false}
      >
        <FullScreenLayout>
          <PageTitle>Assessment Tests</PageTitle>
          <TopBar>
            <FiltersHorizontalWrapper style={{ marginBottom: 0, flex: 1 }}>
              <TextFilterPill
                label="Name"
                value={nameFilter}
                placeholder="Filter by name..."
                onChange={setNameFilter}
              />
            </FiltersHorizontalWrapper>
            <SmallButton
              label="Create New"
              type="primary"
              faIcon="fa-solid fa-plus"
              showIconBeforeLabel
              onClick={() =>
                history.push(ROUTES.ADMIN.CMS_ASSESSMENT_TEST_CREATE.getPath())
              }
            />
          </TopBar>
          {filteredTests.length === 0 ? (
            <EmptyStateText>
              {nameFilter
                ? 'No assessment tests match the current filter.'
                : 'No assessment tests yet.'}
            </EmptyStateText>
          ) : (
            <TableWrapper>
              <Table
                data={filteredTests}
                getRowId={(row) => row.slug}
                columns={columns}
                className="assessment-tests-table"
                getRowProps={(row) => ({
                  onClick: () =>
                    history.push(
                      ROUTES.ADMIN.CMS_ASSESSMENT_TEST_EDIT.getPath({
                        slug: row.original.slug,
                      })
                    ),
                })}
              />
            </TableWrapper>
          )}
        </FullScreenLayout>
      </CMSPageWrapper>
    </PageWithAsyncData>
  );
}
